// Portfolio hub hero, three layouts (hero.layout): split = an accent title card (left) and an image card (right), image = the image fills
// the hero with the title card floating on it, text = one full-width title card. Admins can edit the title and description
// and change the image (upload, or pick one of the uploaded photos) via a hover overlay. Content lives in the database
// (GET/PUT /api/portfolio/hero, see portfolio-media.js).
// The image card follows the open section: the section's own image, else the section's doodle, else the page's hero image.
// With edit mode on, the image buttons change the open section's image (the page image when no section is open).
(function () {
  const { Box, Typography } = MaterialUI;
  const { useState, useEffect, useRef } = React;

  // The server prerenders the home hero into the HTML (see transform in server.js) and embeds its data, so the first render matches it.
  const PRERENDERED = (() => { try { return JSON.parse(document.getElementById('prerender-hero').textContent); } catch (e) { return null; } })();
  const DEFAULTS = { title: 'Andrei-Alexandru Matei', description: 'Video, photography and UX work. Pick a section to browse.', imageUrl: null, canEdit: false };
  const frosted = 'color-mix(in srgb, var(--m3-surface-container) 90%, transparent)';

  function EditDialog({ hero, page, onClose, onSaved }) {
    const { Dialog, Button, TextField, Banner, SegmentedButton, FontPicker } = window.Components;
    const [f, setF] = useState({ title: hero.title, description: hero.description, layout: hero.layout || 'split', titleFont: hero.titleFont || '', descFont: hero.descFont || '' });
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const save = async () => {
      setBusy(true); setError('');
      try { onSaved(await window.PortfolioApi.hero.save({ ...f, imageUrl: hero.imageUrl }, page)); window.notify('Hero saved'); }
      catch (e) { setError(e.message); setBusy(false); }
    };
    return (
      <Dialog
        open onClose={onClose} maxWidth="sm" headline="Edit hero"
        actions={<><Button variant="filled" disabled={busy || !f.title.trim()} onClick={save}>{busy ? 'Saving…' : 'Save'}</Button></>}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Banner severity="error" onClose={() => setError('')}>{error}</Banner>}
          {/* each text with its font (window.SiteFonts; Site default = Admin › Site settings), previewed in the field itself */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField size="small" label="Title" required value={f.title} onChange={e => setF({ ...f, title: e.target.value })} sx={{ flex: '1 1 260px' }} inputProps={{ style: { fontFamily: window.SiteFonts.css(f.titleFont) } }} />
            <FontPicker label="Title font" value={f.titleFont} onChange={titleFont => setF({ ...f, titleFont })} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField size="small" label="Description" multiline rows={3} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} sx={{ flex: '1 1 260px' }} inputProps={{ style: { fontFamily: window.SiteFonts.css(f.descFont) } }} />
            <FontPicker label="Description font" value={f.descFont} onChange={descFont => setF({ ...f, descFont })} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Layout</Typography>
            <SegmentedButton ariaLabel="Hero layout" value={f.layout} onChange={layout => setF({ ...f, layout })}
              options={[{ value: 'split', label: 'Split' }, { value: 'image', label: 'Image' }, { value: 'text', label: 'Text' }]} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {{ split: 'Title card next to the image.', image: 'The image fills the hero and the text sits on it.', text: 'Only the title and text, across the full width.' }[f.layout]}
            </Typography>
          </Box>
        </Box>
      </Dialog>
    );
  }

  // Pick one of the already uploaded portfolio photos as the hero image.
  function PhotoPicker({ current, onClose, onPick }) {
    const { Dialog, Button } = window.Components;
    const [photos, setPhotos] = useState(null);
    useEffect(() => {
      window.PortfolioApi.photos.list().then(r => setPhotos(r.items)).catch(() => setPhotos([]));
    }, []);
    return (
      <Dialog open onClose={onClose} maxWidth="md" headline="Choose a photo">
        {photos === null ? null : photos.length === 0 ? (
          <Typography variant="body1" sx={{ color: 'text.secondary', pt: 1 }}>No photos uploaded yet. Add some in the Photography section, or use “Upload image”.</Typography>
        ) : (
          <window.Components.ImageGrid minWidth={140} sx={{ pt: 1 }}>
            {photos.map(p => (
              <Box
                key={p.id}
                component="button"
                type="button"
                onClick={() => onPick(p.url)}
                aria-label={`Use ${p.title || 'this photo'}`}
                aria-pressed={p.url === current}
                sx={{
                  p: 0, border: 0, cursor: 'pointer', aspectRatio: '4 / 3', overflow: 'hidden', borderRadius: 'var(--m3-shape-medium)',
                  background: 'var(--m3-surface-container-highest)',
                  outline: p.url === current ? '3px solid var(--m3-primary)' : 'none', outlineOffset: -3,
                  '&:hover': { opacity: 0.9 }, '&:focus-visible': { outline: '3px solid var(--m3-secondary)', outlineOffset: 2 }
                }}
              >
                <Box component="img" src={p.thumbUrl || p.url} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </Box>
            ))}
          </window.Components.ImageGrid>
        )}
      </Dialog>
    );
  }

  // preset: fixed content to show ({ title, description, imageUrl, layout }) instead of loading the page's hero (the Admin Console gallery).
  // page: key of the portfolio page whose hero this is ('about' is the original one; other pages start with just their title).
  // defaultPending: the page's sections are still loading and the default one will be shown (PortfolioHub), so the prerendered picture is the right one
  function PortfolioHero({ page = 'about', section = null, onSectionChanged, preset = null, defaultPending = false }) {
    const { Card, Button, IconButton, Tooltip, Banner, GoogleIcon } = window.Components;
    // Starts from the server's prerender (every live page's hero, in `heroes`), so the card keeps its size when the hero loads (CLS)
    const preHero = PRERENDERED && ((PRERENDERED.heroes && PRERENDERED.heroes[page]) || ((PRERENDERED.page || 'about') === page ? PRERENDERED : null));
    const pre = preHero ? { title: preHero.title, description: preHero.description, imageUrl: preHero.imageUrl, layout: preHero.layout, titleFont: preHero.titleFont, descFont: preHero.descFont, canEdit: false } : null;
    const [hero, setHero] = useState(preset ? { canEdit: false, ...preset } : pre || (page === 'about' ? DEFAULTS : { title: '', description: '', imageUrl: null, canEdit: false }));
    const editOn = window.EditMode.use();
    const [dialog, setDialog] = useState(null); // 'edit' | 'pick' | null
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const fileRef = useRef(null);
    const sectionImage = section && section.heroImage ? section.heroImage : null;
    const doodle = window.Doodle.useDoodle(section ? `section:${section.key}` : 'section:-').doodle;
    // Until the sections arrive, the picture the server already put in the page (and requested): no empty card, no second download
    const image = !section && defaultPending && preHero && preHero.image !== undefined ? preHero.image : sectionImage || (doodle ? null : hero.imageUrl);
    const ownImage = section ? sectionImage : hero.imageUrl; // the picture Upload / Choose replace
    // What Remove clears: the section's image, or, when the card is showing the page-wide fallback image, that one
    const removable = sectionImage ? 'section' : (image && hero.imageUrl && image === hero.imageUrl ? 'page' : null);
    const removeImage = async () => {
      if (removable === 'section') return saveImage(null);
      setBusy(true); setError('');
      try { setHero(await window.PortfolioApi.hero.save({ title: hero.title, description: hero.description, imageUrl: null, titleFont: hero.titleFont || '', descFont: hero.descFont || '' }, page)); }
      catch (e) { setError(e.message); }
      setBusy(false);
    };
    const firstImage = useRef(image); // later pictures fade in; the first one paints at once (it is the page's LCP)

    useEffect(() => {
      if (!preset) window.PortfolioApi.hero.get(page).then(setHero).catch(() => {});
    }, [page]);

    const applyImage = async (imageUrl) => {
      if (section) { await window.PortfolioApi.sections.setImage(section.key, imageUrl); if (onSectionChanged) await onSectionChanged(); }
      else setHero(await window.PortfolioApi.hero.save({ title: hero.title, description: hero.description, imageUrl, titleFont: hero.titleFont || '', descFont: hero.descFont || '' }, page));
      window.notify(imageUrl ? 'Image updated' : 'Image removed');
    };
    const saveImage = async (imageUrl) => {
      setBusy(true); setError('');
      try { await applyImage(imageUrl); }
      catch (e) { setError(e.message); }
      setBusy(false);
    };
    const upload = async (file) => {
      if (!file) return;
      setBusy(true); setError('');
      try {
        const { url } = await window.PortfolioApi.upload(file);
        await applyImage(url);
      } catch (e) { setError(e.message); }
      setBusy(false);
    };

    const layout = hero.layout === 'image' || hero.layout === 'text' ? hero.layout : 'split';
    const mayEdit = hero.canEdit && editOn;
    const titleText = (
      <>
        <Typography id={preset ? undefined : 'portfolio-title'} variant="h1" component={preset ? 'h2' : 'h1'} sx={{ mb: { xs: 1, md: 2 }, fontWeight: 400, ...(hero.titleFont && { fontFamily: window.SiteFonts.css(hero.titleFont), fontSynthesis: 'none' }), fontSize: { xs: '2.25rem', md: layout === 'image' ? '3rem' : '3.5rem' }, lineHeight: 1.1, letterSpacing: '-0.5px', color: 'inherit' }}>
          {hero.title}
        </Typography>
        {hero.description && (
          <Typography variant="h6" component="p" sx={{ color: 'inherit', fontWeight: 400, ...(hero.descFont && { fontFamily: window.SiteFonts.css(hero.descFont), fontSynthesis: 'none' }), maxWidth: layout === 'text' ? 960 : 560, fontSize: { xs: '1.05rem', md: '1.25rem' }, lineHeight: { xs: 1.4, md: 1.6 } }}>{hero.description}</Typography>
        )}
      </>
    );
    const editTitle = mayEdit && (
      <Tooltip title="Edit hero">
        <IconButton aria-label="Edit hero" onClick={() => setDialog('edit')} sx={{ position: 'absolute', top: 12, right: layout === 'image' ? 'auto' : 12, left: layout === 'image' ? 12 : 'auto', color: layout === 'image' ? 'var(--m3-on-surface)' : 'inherit', zIndex: 2, ...(layout === 'image' && { backgroundColor: frosted }) }}>
          <GoogleIcon name="edit" size={22} style={{ color: 'inherit' }} />
        </IconButton>
      </Tooltip>
    );
    const picture = (
      <>
        {!image && !sectionImage && doodle && <window.Components.DoodleArt key={section.key} config={doodle} />}
        {image && (
          <Box
            key={image} component="img" src={image}
            {...(/^\/uploads\/portfolio\/[\w.-]+\.(?!gif$)\w+$/i.test(image) ? { srcSet: `${image}?w=480 480w, ${image}?w=800 800w, ${image}?w=1200 1200w, ${image} 1600w`, sizes: layout === 'image' ? '100vw' : '(max-width: 900px) 100vw, 50vw' } : {})}
            alt="" fetchpriority="high" decoding="async"
            sx={{
              position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
              ...(image !== firstImage.current && { animation: 'heroImageIn var(--m3-duration-long) var(--m3-easing-standard)', '@keyframes heroImageIn': { from: { opacity: 0 }, to: { opacity: 1 } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } })
            }}
          />
        )}
      </>
    );
    const imageActions = mayEdit && (
      <Box
        className="hero-actions"
        role="toolbar"
        aria-label={section ? `Image for ${section.title}` : 'Hero image'}
        sx={{
          position: 'absolute', top: 12, right: 12, zIndex: 2, p: 0.5, display: 'flex', gap: 0.5,
          borderRadius: 'var(--m3-shape-full)', backgroundColor: frosted, backdropFilter: 'blur(12px)',
          transition: 'opacity var(--m3-duration-short) var(--m3-easing-standard)'
        }}
      >
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif" hidden onChange={e => { upload(e.target.files[0]); e.target.value = ''; }} />
        <Tooltip title={busy ? 'Working…' : 'Upload image'}><span><IconButton aria-label="Upload image" disabled={busy} onClick={() => fileRef.current.click()}><GoogleIcon name="upload" size={22} /></IconButton></span></Tooltip>
        <Tooltip title="Choose from photos"><span><IconButton aria-label="Choose from photos" disabled={busy} onClick={() => setDialog('pick')}><GoogleIcon name="photo_library" size={22} /></IconButton></span></Tooltip>
        {removable && <Tooltip title={removable === 'page' ? 'Remove page image (the default for every section)' : 'Remove section image'}><span><IconButton aria-label={removable === 'page' ? 'Remove page image' : 'Remove section image'} disabled={busy} onClick={removeImage}><GoogleIcon name="delete" size={22} /></IconButton></span></Tooltip>}
      </Box>
    );
    const imageHover = { '& .hero-actions': { opacity: image || doodle ? 0 : 1 }, '&:hover .hero-actions, &:has(:focus-visible) .hero-actions': { opacity: 1 }, '@media (hover: none)': { '& .hero-actions': { opacity: 1 } } };
    const accent = { backgroundColor: 'var(--m3-primary-container)', color: 'var(--m3-on-primary-container)' };

    return (
      <>
        {error && <Banner severity="error" onClose={() => setError('')} sx={{ mb: 1 }}>{error}</Banner>}
        {layout === 'split' && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch', gap: 1, mb: 1 }}>
            {/* Title card: carries the accent */}
            <Card variant="filled" sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: { md: 340 }, p: { xs: 2.5, md: 6 }, borderRadius: 'var(--m3-shape-extra-large)', ...accent }}>
              {editTitle}
              {titleText}
            </Card>
            {/* Image card */}
            <Card variant="filled" sx={{ flex: { md: 1 }, position: 'relative', minHeight: { xs: 120, md: 'auto' }, aspectRatio: { xs: '21 / 9', md: 'auto' }, borderRadius: 'var(--m3-shape-extra-large)', backgroundColor: 'var(--m3-surface-container-low)', ...imageHover }}>
              {picture}
              {imageActions}
            </Card>
          </Box>
        )}
        {layout === 'image' && (
          // One card: the image fills it and the title card floats on it (frosted surface, so it reads on any picture)
          <Card variant="filled" sx={{ position: 'relative', display: 'flex', alignItems: 'flex-end', minHeight: { xs: 320, md: 440 }, mb: 1, borderRadius: 'var(--m3-shape-extra-large)', backgroundColor: image || doodle ? 'var(--m3-surface-container-low)' : 'var(--m3-primary-container)', ...imageHover }}>
            {picture}
            {imageActions}
            <Box sx={{ position: 'relative', zIndex: 1, m: { xs: 1.5, md: 3 }, p: { xs: 2.5, md: 4 }, maxWidth: 640, borderRadius: 'var(--m3-shape-extra-large)', backgroundColor: frosted, backdropFilter: 'blur(16px)', color: 'var(--m3-on-surface)' }}>
              {editTitle}
              {titleText}
            </Box>
          </Card>
        )}
        {layout === 'text' && (
          <Card variant="filled" sx={{ position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: { md: 240 }, p: { xs: 2.5, md: 6 }, mb: 1, borderRadius: 'var(--m3-shape-extra-large)', ...accent }}>
            {editTitle}
            {titleText}
          </Card>
        )}

        {dialog === 'edit' && <EditDialog hero={hero} page={page} onClose={() => setDialog(null)} onSaved={h => { setHero(h); setDialog(null); }} />}
        {dialog === 'pick' && <PhotoPicker current={ownImage} onClose={() => setDialog(null)} onPick={url => { setDialog(null); saveImage(url); }} />}
      </>
    );
  }

  window.Components = window.Components || {};
  window.Components.PortfolioHero = PortfolioHero;
})();
